import { useState, useEffect, useRef } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Camera, Upload, CalendarIcon, Check, Shirt, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import { authFetch } from "../context/AuthContext";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DailyLog() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [photoBase64, setPhotoBase64] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [wardrobeItems, setWardrobeItems] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [existingLog, setExistingLog] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchWardrobeItems();
  }, []);

  useEffect(() => {
    checkExistingLog();
  }, [selectedDate]);

  const fetchWardrobeItems = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/wardrobe`, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setWardrobeItems(data);
      }
    } catch (error) {
      console.error("Failed to fetch wardrobe:", error);
    }
  };

  const checkExistingLog = async () => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    try {
      const response = await authFetch(`${API_URL}/api/daily-logs/${dateStr}`, {
        
      });
      if (response.ok) {
        const log = await response.json();
        setExistingLog(log);
        setSelectedItems(log.confirmed_items || []);
      } else {
        setExistingLog(null);
        setSelectedItems([]);
      }
    } catch (error) {
      setExistingLog(null);
      setSelectedItems([]);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image must be less than 10MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        setPhotoBase64(base64);
        setAnalysisResult(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAnalyze = async () => {
    if (!photoBase64) {
      toast.error("Please upload a photo first");
      return;
    }

    if (wardrobeItems.length === 0) {
      toast.error("Add items to your wardrobe first to enable AI detection");
      return;
    }

    setAnalyzing(true);
    try {
      const response = await authFetch(`${API_URL}/api/daily-logs/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          photo_base64: photoBase64,
          date: format(selectedDate, "yyyy-MM-dd"),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setAnalysisResult(result);
        setSelectedItems(result.detected_items || []);
        toast.success("Photo analyzed!");
      } else {
        throw new Error("Analysis failed");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze photo");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleConfirm = async () => {
    if (selectedItems.length === 0) {
      toast.error("Select at least one item you wore");
      return;
    }

    setConfirming(true);
    try {
      const response = await authFetch(`${API_URL}/api/daily-logs/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          date: format(selectedDate, "yyyy-MM-dd"),
          confirmed_items: selectedItems,
          photo_base64: photoBase64,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Show streak toast if streak increased
        if (data.streak?.increased && data.streak?.streak > 0) {
          const streakNum = data.streak.streak;
          const fireEmoji = streakNum >= 3 ? "🔥 " : "";
          toast.success(`Upload streak ${fireEmoji}${streakNum} days`, {
            duration: 2000,
          });
        } else {
          toast.success("Outfit logged successfully!");
        }
        
        setExistingLog({ confirmed_items: selectedItems });
        setPhotoBase64(null);
        setAnalysisResult(null);
        fetchWardrobeItems(); // Refresh to show updated wear counts
      } else {
        throw new Error("Confirmation failed");
      }
    } catch (error) {
      console.error("Confirm error:", error);
      toast.error("Failed to save outfit");
    } finally {
      setConfirming(false);
    }
  };

  const toggleItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="daily-log-page">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
              Daily Log
            </h1>
            <p className="text-muted-foreground">
              Upload your outfit and log what you wore
            </p>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="date-picker-btn">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(selectedDate, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Existing Log Notice */}
        {existingLog && (
          <div className="bg-sand-50 border border-border p-4 rounded-sm flex items-center gap-3">
            <Check className="w-5 h-5 text-terracotta-500" />
            <div>
              <p className="font-medium text-primary">
                Outfit already logged for {format(selectedDate, "MMMM d, yyyy")}
              </p>
              <p className="text-sm text-muted-foreground">
                {existingLog.confirmed_items?.length || 0} items worn
              </p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Photo Upload Section */}
          <Card className="border border-border shadow-none p-6">
            <h2 className="font-heading text-xl font-medium mb-4">Upload Outfit Photo</h2>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid="photo-upload-input"
            />

            {photoBase64 ? (
              <div className="space-y-4">
                <div className="aspect-[3/4] max-h-[400px] relative overflow-hidden rounded-sm bg-sand-50">
                  <img
                    src={`data:image/jpeg;base64,${photoBase64}`}
                    alt="Outfit"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 rounded-sm"
                  >
                    Change Photo
                  </Button>
                  <Button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                    data-testid="analyze-photo-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Camera className="w-4 h-4 mr-2" />
                        Analyze Photo
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="upload-zone aspect-[3/4] max-h-[400px] flex flex-col items-center justify-center cursor-pointer"
                data-testid="upload-zone"
              >
                <Upload className="w-12 h-12 text-sand-300 mb-4" />
                <p className="font-medium text-primary mb-1">Upload your outfit photo</p>
                <p className="text-sm text-muted-foreground">JPEG, PNG, or WebP up to 10MB</p>
              </div>
            )}

            {/* AI Analysis Result */}
            {analysisResult && (
              <div className="mt-4 p-4 bg-sand-50 rounded-sm">
                <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                  AI Analysis
                </p>
                <p className="text-sm text-primary">{analysisResult.ai_analysis}</p>
              </div>
            )}
          </Card>

          {/* Item Selection Section */}
          <Card className="border border-border shadow-none p-6">
            <h2 className="font-heading text-xl font-medium mb-4">
              Select Items Worn
              {selectedItems.length > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({selectedItems.length} selected)
                </span>
              )}
            </h2>

            {wardrobeItems.length > 0 ? (
              <>
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                  {wardrobeItems.map((item) => (
                    <div
                      key={item.item_id}
                      className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                        selectedItems.includes(item.item_id)
                          ? "border-terracotta-500 bg-terracotta-500/5"
                          : "border-border hover:bg-sand-50"
                      }`}
                      onClick={() => toggleItem(item.item_id)}
                      data-testid={`select-item-${item.item_id}`}
                    >
                      <Checkbox
                        checked={selectedItems.includes(item.item_id)}
                        onCheckedChange={() => toggleItem(item.item_id)}
                        className="pointer-events-none"
                      />
                      <div className="w-12 h-12 bg-sand-50 flex items-center justify-center flex-shrink-0 rounded-sm overflow-hidden">
                        {item.image_base64 ? (
                          <img
                            src={`data:image/jpeg;base64,${item.image_base64}`}
                            alt={item.item_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Shirt className="w-6 h-6 text-sand-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {item.brand} {item.item_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.wear_count || 0} wears • ${item.cost_per_wear?.toFixed(2)}/wear
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handleConfirm}
                  disabled={confirming || selectedItems.length === 0}
                  className="w-full mt-4 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 font-medium tracking-wide text-sm uppercase"
                  data-testid="confirm-outfit-btn"
                >
                  {confirming ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Confirm Outfit ({selectedItems.length} items)
                    </>
                  )}
                </Button>
              </>
            ) : (
              <div className="text-center py-12 bg-sand-50 rounded-sm">
                <Shirt className="w-10 h-10 mx-auto mb-3 text-sand-300" />
                <p className="text-muted-foreground">
                  Add items to your wardrobe first
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
